/* ══ THE MEDIA EDITOR ════════════════════════════════════════════════════════
   Editing a photo or a clip in the browser, for a thing that will only ever be
   looked at on a phone.

   ══ WHY THE FIRST ONE WAS WRONG, ALL THE WAY DOWN ══════════════════════════
   🟥 THE CANVAS WAS THE BROWSER WINDOW, AND THE COORDINATES ARE PHONE POINTS.
      A sticker is stored as an offset from the centre of the picture in the
      phone's own points; the viewer multiplies by `width / 390` to draw it. The
      old editor made its stage as wide as the window, so the scale factor was
      2 or 3, and EVERYTHING inherited that mistake:
        · a poll card 220 points wide was drawn 600px wide and ran off the stage
          — which is why it "did not show";
        · the loving indicator, dropped 100 points above centre, landed above
          the top edge of a short stage and had to be dragged down to be seen;
        · and where you dropped something bore no relation to where it would
          land on a phone, which is the thing an editor exists to tell you.
      Every one of those is the same bug wearing different clothes.

   🟥 AND THE VIDEO NEVER PLAYED — a bare `<video>` with no autoplay and no
      control, so a clip was edited against a frozen first frame.

   🟥 AND THE TEXT LOOKS WERE WRITTEN FROM THEIR NAMES rather than from the
      phone. `neon` kept the text in its own colour under a weak halo where the
      phone paints it white and glows it in the chosen colour. See
      `overlayTextStyle` in ui/Overlay.jsx, which is now the phone's numbers.

   ══ WHAT THIS IS INSTEAD ═══════════════════════════════════════════════════
   🟢 THE CANVAS IS A PHONE. A real device frame at its real point size, and you
      drag inside it. The scale factor is 1 on an iPhone by construction, so
      where you put something is where it goes — there is no mapping to get
      wrong. Zoom scales the FRAME, never the coordinates.

   🟢 AND THE CANVAS IS THE PREVIEW, because they are the same object. The reel
      chrome is drawn over it — the rail of buttons, the byline, the caption,
      the music line, the status bar — so you can see what your sticker is about
      to sit behind. A separate preview pane would be a second renderer to keep
      in step, and the first thing it would drift on is the one thing it is for.

   🟢 AND YOU CAN CHECK ANOTHER DEVICE. The viewer's scale is `width / 390`, so
      a bigger screen draws everything proportionally bigger in the same
      relative place — switching the frame to an iPad or a tall Android shows
      exactly that, using the same renderer, rather than promising it. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EDITOR_FILTERS, WIDGET_KINDS, newSticker, filterColorOf,
  TEXT_MODES, TEXT_COLORS, TEXT_SIZES,
} from '../services/mediaEditor';
import { StickerBody, stickerScale, overlayTextStyle, REF_W } from './Overlay';
import EmojiPanel from './EmojiPanel';
/* 🎵 a song under the clip — the phone's Sounds drawer, ported; see ui/SoundPanel */
import SoundPanel from './SoundPanel';
import { soundFieldsFor, soundCredited, MIX_DEFAULT } from '../services/soundLibrary';
import {
  X, Check, Image as ImageIcon, Smile, Type as TypeIcon, Sliders, Scissors,
  Trash, Play, Pause, Heart, Comment, Forward, Bookmark,
} from './icons';
import './mediaEditor.css';

/* ── the devices a post is actually looked at on ──────────────────────────── */

/**
 * 🟥 THE WIDTHS ARE THE POINT, not the pixel counts. `REF_W` is 390 because
 *    that is the phone the stored offsets are in; every other device is a
 *    multiplier on it, which is exactly what the viewer computes. Listing real
 *    devices rather than "small / medium / large" is what makes the preview a
 *    statement about the world rather than about this file.
 */
export const DEVICES = [
  { id: 'iphone', name: 'iPhone', w: 390, h: 844, chin: 34, top: 'island' },
  { id: 'max', name: 'iPhone Max', w: 430, h: 932, chin: 34, top: 'island' },
  { id: 'android', name: 'Android', w: 412, h: 915, chin: 24, top: 'punch' },
  { id: 'ipad', name: 'iPad', w: 820, h: 1180, chin: 20, top: 'bar' },
];

export function deviceById(id) {
  for (const d of DEVICES) if (d.id === id) return d;
  return DEVICES[0];
}

/* ── one file's edits ─────────────────────────────────────────────────────── */

/** The defaults a post carries when nobody opened an editor at all. */
export function blankEdit() {
  return {
    filter: 'pure',
    intensity: 100,
    text: '',
    color: '#FFFFFF',
    mode: 'shadow',
    align: 'center',
    size: TEXT_SIZES.med,
    pos: { x: 0, y: 0 },
    stickers: [],
    trim: null,
    /* 🎵 { track, startMs, mix } — or null for a clip that keeps its own sound */
    sound: null,
  };
}

/**
 * One file's edits, as the fields a post document carries.
 *
 * 🟥 THE NAMES ARE THE PHONE'S NAMES. A browser that invented tidier ones would
 *    produce posts the phone renders as blank pictures.
 */
export function editToFields(e) {
  const d = e || blankEdit();
  return {
    filterName: d.filter,
    filterColor: filterColorOf(d.filter),
    filterIntensity: d.intensity,
    overlayText: d.text || '',
    overlayTextColor: d.color,
    overlayTextStyle: d.mode,
    overlayTextBg: d.mode === 'box',
    overlayTextAlign: d.align,
    overlayTextSize: d.size,
    overlayTextFont: 'System',
    overlayTextX: d.pos.x,
    overlayTextY: d.pos.y,
    stickers: d.stickers,
    ...(d.trim ? { trimStartMs: Math.round(d.trim.start), trimEndMs: Math.round(d.trim.end) } : {}),
    /* 🎵 the sound fields — the credit, the licence, the start and the mix — from
       the one writer both clients use */
    ...(d.sound && d.sound.track
      ? { ...soundFieldsFor(d.sound.track, d.sound.mix || MIX_DEFAULT, d.sound.startMs || 0), ...(soundCredited(d.sound.track, d.sound.mix || MIX_DEFAULT) ? { audioTrackId: String(d.sound.track.id) } : {}) }
      : {}),
  };
}

/** Did anybody actually change anything? */
export function isEdited(e) {
  if (!e) return false;
  return e.filter !== 'pure' || !!(e.text || '').trim() || (e.stickers || []).length > 0 || !!e.trim || !!(e.sound && e.sound.track);
}

/** A post-shaped object, so the viewer's own renderer can draw the draft. */
export function draftPost(e, me, caption) {
  return {
    id: 'draft',
    userId: me?.uid || 'draft',
    userName: me?.fullName || me?.username || 'you',
    userImage: me?.profileImage || '',
    content: caption || '',
    ...editToFields(e),
  };
}

/* ── dragging, in phone points ────────────────────────────────────────────── */

/**
 * 🟥 THE POINTER MOVES IN SCREEN PIXELS AND THE STICKER LIVES IN PHONE POINTS.
 *    `unit` is how many screen pixels one stored point is worth — the device's
 *    own scale times the zoom — and dividing by it is the whole reason a drag
 *    lands where the finger went. Getting this wrong is what the old editor did
 *    globally by making the stage the window.
 *
 * 🟥 POINTER CAPTURE, NOT DOCUMENT LISTENERS. The frame is small and surrounded
 *    by panels; without capture the sticker stops following the moment the
 *    pointer leaves it, which is most of the drags anybody makes.
 *
 * 🟥 AND THE OFFSET IS TAKEN AT GRAB, so the sticker does not jump its centre
 *    under the pointer on the first pixel of every drag.
 */
function useDragger(unit, onMove) {
  const from = useRef(null);
  const down = (e, cur) => {
    e.stopPropagation();
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not captured; the drag still works while inside */ }
    from.current = { px: e.clientX, py: e.clientY, x: cur.x, y: cur.y };
  };
  const move = (e) => {
    const f = from.current;
    if (!f || !unit) return;
    e.stopPropagation();
    onMove({ x: f.x + (e.clientX - f.px) / unit, y: f.y + (e.clientY - f.py) / unit });
  };
  const up = (e) => { if (from.current) { e.stopPropagation(); from.current = null; } };
  return { down, move, up };
}

/* ── the reel chrome, drawn over the draft ────────────────────────────────── */

/**
 * 🟢 EVERY PIECE OF THIS IS `pointer-events: none`. It is a picture of the
 *    player, not the player — its only job is to show you what a sticker is
 *    about to be hidden behind, and a rail that swallowed drags would defeat
 *    the canvas it is drawn on.
 */
function ReelChrome({ me, caption, device }) {
  const name = me?.fullName || me?.username || 'you';
  return (
    <div className="mx-chrome" aria-hidden="true">
      <div className={`mx-status ${device.top}`}>
        <span>9:41</span>
        <i className="mx-notch" />
        <span className="mx-status-r">
          <b className="mx-sig" /><b className="mx-wifi" /><b className="mx-batt" />
        </span>
      </div>

      <div className="mx-rail">
        <span className="mx-ava">
          {me?.profileImage ? <img src={me.profileImage} alt="" /> : <em>{name.charAt(0).toUpperCase()}</em>}
          <u>+</u>
        </span>
        <span className="mx-act"><Heart width={30} height={30} /><i>12.4K</i></span>
        <span className="mx-act"><Comment width={29} height={29} /><i>208</i></span>
        <span className="mx-act"><Forward width={29} height={29} /><i>96</i></span>
        <span className="mx-act"><Bookmark width={28} height={28} /><i>41</i></span>
        <span className="mx-disc" />
      </div>

      <div className="mx-foot" style={{ paddingBottom: `${device.chin + 14}px` }}>
        <b>@{name}</b>
        {caption ? <p>{caption}</p> : null}
        <span className="mx-music">
          <em>♪</em> Original sound — {name}
        </span>
      </div>
      <span className="mx-home" />
    </div>
  );
}

/* ── a sticker while it is being placed ───────────────────────────────────── */

function DraftSticker({ s, scale, unit, selected, onSelect, onChange, onDelete }) {
  const drag = useDragger(unit, (p) => onChange({ ...s, x: p.x, y: p.y }));
  const typed = s.type === 'poll' || s.type === 'slider' || s.type === 'location';
  const k = stickerScale(s);
  /* 🟢 THE HANDLES DO NOT SCALE WITH THE STICKER. A delete cross on one pinched
     to 0.35 would be four pixels across, and on one blown to 5× it would be
     enormous. They are counter-scaled so they stay the size a pointer needs. */
  const chrome = { transform: `scale(${1 / (k * Math.max(0.2, scale))})` };

  return (
    <span
      className={`mx-sticker ${selected ? 'on' : ''}`}
      style={{ transform: `translate(-50%, -50%) translate(${s.x * scale}px, ${s.y * scale}px) scale(${k})` }}
      onPointerDown={(e) => { onSelect(); drag.down(e, s); }}
      onPointerMove={drag.move}
      onPointerUp={drag.up}
      onPointerCancel={drag.up}
    >
      {typed
        ? <TypedSticker s={s} scale={scale} onChange={onChange} />
        : <StickerBody s={s} scale={scale} interactive={false} />}

      {selected && (
        <button
          type="button" className="mx-sticker-x" style={chrome}
          title="Remove" aria-label="Remove sticker"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
        >
          <X width={12} height={12} />
        </button>
      )}
    </span>
  );
}

/**
 * The three you have to type into.
 *
 * 🟢 EVERYTHING ELSE IS DRAWN BY THE VIEWER'S OWN RENDERER (`StickerBody`), so
 *    what you place is literally what everybody else will see. Only these three
 *    differ, because a reader gets a poll to answer and an author gets fields.
 */
function TypedSticker({ s, scale, onChange }) {
  const stop = (e) => e.stopPropagation();
  if (s.type === 'location') {
    return (
      <span className="vb-ov-pill round mx-typed" style={{ padding: `${10 * scale}px ${20 * scale}px`, borderRadius: `${20 * scale}px`, fontSize: `${Math.max(6, 20 * scale)}px` }}>
        <svg viewBox="0 0 24 24" width={Math.max(6, 24 * scale)} height={Math.max(6, 24 * scale)} fill="currentColor" aria-hidden="true">
          <path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5Z" />
        </svg>
        <input
          value={s.locationText || ''} placeholder="Type a place…" aria-label="Location"
          onPointerDown={stop}
          onChange={(e) => onChange({ ...s, locationText: e.target.value })}
        />
      </span>
    );
  }
  if (s.type === 'poll') {
    return (
      <span className="vb-ov-poll mx-typed" style={{ width: `${220 * scale}px`, padding: `${20 * scale}px`, borderRadius: `${16 * scale}px` }}>
        <input
          className="mx-q" value={s.question || ''} placeholder="Ask a question..." aria-label="Poll question"
          onPointerDown={stop}
          onChange={(e) => onChange({ ...s, question: e.target.value })}
        />
        <span className="vb-ov-pollrow" style={{ gap: `${10 * scale}px` }}>
          {[0, 1].map((i) => (
            <input
              key={i} className="mx-opt"
              /* 🟥 `??`, NOT `||`. The phone deliberately allows an option to be
                 blanked, and `||` resurrects "Yes"/"No" over the author's own
                 empty string on every keystroke that clears the field. */
              value={s.options?.[i] ?? (i === 0 ? 'Yes' : 'No')}
              aria-label={`Option ${i + 1}`}
              onPointerDown={stop}
              onChange={(e) => {
                const next = [s.options?.[0] ?? 'Yes', s.options?.[1] ?? 'No'];
                next[i] = e.target.value;
                onChange({ ...s, options: next });
              }}
            />
          ))}
        </span>
      </span>
    );
  }
  return (
    <span className="vb-ov-slider mx-typed" style={{ width: `${250 * scale}px`, padding: `${20 * scale}px`, borderRadius: `${16 * scale}px` }}>
      <input
        className="mx-q light" value={s.question || ''} placeholder="Ask anything..." aria-label="Question"
        onPointerDown={stop}
        onChange={(e) => onChange({ ...s, question: e.target.value })}
      />
      <span className="vb-ov-track" style={{ height: `${Math.max(4, 40 * scale)}px` }}>
        <i className="vb-ov-rail" style={{ height: `${Math.max(2, 8 * scale)}px`, borderRadius: `${4 * scale}px` }}><u style={{ width: '0%' }} /></i>
        <em className="vb-ov-face" style={{ left: '0%', fontSize: `${Math.max(8, 40 * scale)}px` }}>😍</em>
      </span>
    </span>
  );
}

/* ── the phone ────────────────────────────────────────────────────────────── */

function Phone({
  file, url, edit, set, device, zoom, videoRef, me, caption, sel, setSel, playing,
}) {
  /* the viewer's own rule: a stored point is `width / 390` screen points here */
  const scale = device.w / REF_W;
  const unit = scale * zoom;
  const isVideo = !!file?.type?.startsWith('video/');
  const tint = filterColorOf(edit.filter);
  const textDrag = useDragger(unit, (p) => set({ ...edit, pos: p }));

  const changeSticker = (next) => set({ ...edit, stickers: edit.stickers.map((x) => (x.id === next.id ? next : x)) });
  const removeSticker = (id) => set({ ...edit, stickers: edit.stickers.filter((x) => x.id !== id) });

  return (
    <div
      className="mx-device"
      style={{ width: `${device.w}px`, height: `${device.h}px`, transform: `scale(${zoom})` }}
    >
      <div className="mx-screen" onPointerDown={() => setSel(null)}>
        {isVideo
          ? (
            /* 🟥 IT PLAYS. The old one was a bare `<video>` with no autoplay and
               no control at all, so a clip was edited against a frozen first
               frame — you could not see what you were decorating. */
            <video ref={videoRef} src={url} playsInline loop muted autoPlay />
          )
          : <img src={url} alt="" />}

        {tint !== 'transparent' && (
          <span className="mx-tint" style={{ background: tint, opacity: edit.intensity / 100, mixBlendMode: 'overlay' }} />
        )}

        {edit.stickers.map((s) => (
          <DraftSticker
            key={s.id} s={s} scale={scale} unit={unit}
            selected={sel === s.id}
            onSelect={() => setSel(s.id)}
            onChange={changeSticker}
            onDelete={removeSticker}
          />
        ))}

        {!!edit.text.trim() && (
          <span
            className={`mx-text a-${edit.align}`}
            style={{
              transform: `translate(-50%, 0) translate(${edit.pos.x * scale}px, ${edit.pos.y * scale}px)`,
              ...overlayTextStyle({
                overlayTextStyle: edit.mode,
                overlayTextSize: edit.size,
                overlayTextColor: edit.color,
                overlayTextAlign: edit.align,
              }, scale),
            }}
            onPointerDown={(e) => textDrag.down(e, edit.pos)}
            onPointerMove={textDrag.move}
            onPointerUp={textDrag.up}
            onPointerCancel={textDrag.up}
          >
            {edit.text}
          </span>
        )}

        <ReelChrome me={me} caption={caption} device={device} />
        {!playing && isVideo && <span className="mx-paused" aria-hidden="true"><Play width={26} height={26} /></span>}
      </div>
    </div>
  );
}

/* ── trim ─────────────────────────────────────────────────────────────────── */

const fmt = (ms) => {
  const t = Math.max(0, ms) / 1000;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}.${Math.floor((t * 10) % 10)}`;
};

/**
 * 🟥 THE TWO HANDLES CANNOT CROSS, and the floor is half a second. A range of
 *    nothing is not a shorter clip, it is a video paused on one frame forever.
 */
export function clampTrim(start, end, dur) {
  const MIN = 500;
  const d = Math.max(0, Number(dur) || 0);
  let s = Math.min(Math.max(0, Number(start) || 0), Math.max(0, d - MIN));
  let e = Math.max(Math.min(d, Number(end) || d), s + MIN);
  if (e > d) { e = d; s = Math.min(s, Math.max(0, d - MIN)); }
  return { start: s, end: e };
}

function TrimPanel({ videoRef, edit, set, dur, at }) {
  const barRef = useRef(null);
  const trim = edit.trim || { start: 0, end: dur };

  const posAt = (clientX) => {
    const el = barRef.current;
    if (!el || !dur) return 0;
    const r = el.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * dur;
  };

  const grab = (which) => (e) => {
    e.preventDefault();
    const el = e.currentTarget;
    try { el.setPointerCapture?.(e.pointerId); } catch { /* pointer stays inside */ }
    const move = (ev) => {
      const ms = posAt(ev.clientX);
      set({ ...edit, trim: which === 'start' ? clampTrim(ms, trim.end, dur) : clampTrim(trim.start, ms, dur) });
    };
    move(e);
    el.onpointermove = move;
    el.onpointerup = (ev) => { ev.currentTarget.onpointermove = null; ev.currentTarget.onpointerup = null; };
  };

  if (!dur) return <p className="mx-hint">Reading the clip…</p>;
  const pct = (ms) => `${(ms / dur) * 100}%`;

  return (
    <>
      <div className="mx-trimbar" ref={barRef}>
        <span className="mx-trimkeep" style={{ left: pct(trim.start), right: `${100 - (trim.end / dur) * 100}%` }} />
        <span className="mx-playhead" style={{ left: pct(at) }} />
        <button type="button" className="mx-trimh" style={{ left: pct(trim.start) }} aria-label="Trim start" onPointerDown={grab('start')} />
        <button type="button" className="mx-trimh" style={{ left: pct(trim.end) }} aria-label="Trim end" onPointerDown={grab('end')} />
      </div>
      <div className="mx-trimnums">
        <b>{fmt(trim.start)}</b>
        <i>{fmt(trim.end - trim.start)}</i>
        <b>{fmt(trim.end)}</b>
      </div>
      {edit.trim && (
        <button type="button" className="mx-plain" onClick={() => set({ ...edit, trim: null })}>Use the whole clip</button>
      )}
      {/* 🟥 SAID OUT LOUD, because it is a real difference from the phone: the
          browser stores the range instead of cutting the file. Both clients
          honour it, so the clip plays trimmed everywhere — but the upload is
          still the whole video, and somebody choosing between the two clients
          deserves to know which one shortens the file. */}
      <p className="mx-hint">The clip uploads whole and plays from your cut — you can change it later.</p>
    </>
  );
}

/* ── the editor ───────────────────────────────────────────────────────────── */

export default function MediaEditor({ files, initial, me, caption, onCancel, onDone }) {
  const [i, setI] = useState(0);
  const [edits, setEdits] = useState(() => files.map((_, n) => (initial?.[n] ? { ...initial[n] } : blankEdit())));
  const [tool, setTool] = useState('stickers');
  const [deviceId, setDeviceId] = useState('iphone');
  const [zoom, setZoom] = useState(0);          // 0 = fit
  const [manualZoom, setManualZoom] = useState(null);
  const [sel, setSel] = useState(null);
  const [playing, setPlaying] = useState(true);
  const [dur, setDur] = useState(0);
  const [at, setAt] = useState(0);
  const videoRef = useRef(null);
  const stageRef = useRef(null);
  const stickerFileRef = useRef(null);

  const device = deviceById(deviceId);

  /* 🟥 CREATED ONCE PER FILE LIST, AND REVOKED. Building them during render
     leaks a blob URL on every keystroke in the text box. */
  const urls = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);

  const edit = edits[i] || blankEdit();
  const set = useCallback((next) => {
    setEdits((prev) => prev.map((e, n) => (n === i ? next : e)));
  }, [i]);

  const isVideo = !!files[i]?.type?.startsWith('video/');
  useEffect(() => { if (!isVideo && (tool === 'trim' || tool === 'sound')) setTool('stickers'); }, [isVideo, tool]);
  useEffect(() => { setSel(null); }, [i]);

  /* 🟢 THE FRAME IS SCALED TO FIT, and the coordinates never are. Zoom is a
     property of the picture frame; a stored point is a stored point. */
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const read = () => {
      /* 🟥 THE STRIP UNDER THE PHONE IS PART OF THE STAGE. It is absolutely
         positioned, so it does not shrink `clientHeight`, and fitting to the
         full height cropped the bottom of the device off — bezel, home bar and
         the last of the caption. The bezel is real height too: 11px of padding
         and an 11px outer ring on each side. */
      const CHROME_H = 56;   // the zoom/pages strip
      const BEZEL = 44;      // 11 padding + 11 ring, both sides
      const z = Math.min(
        (el.clientWidth - 64 - BEZEL) / device.w,
        (el.clientHeight - CHROME_H - 48 - BEZEL) / device.h,
      );
      setZoom(Math.max(0.25, Math.min(1, z)));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [device.w, device.h]);
  const z = manualZoom || zoom || 0.5;

  /* ── the clip's own clock ── */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isVideo) { setDur(0); setAt(0); return undefined; }
    const meta = () => setDur(Number.isFinite(v.duration) ? v.duration * 1000 : 0);
    const tick = () => {
      const ms = v.currentTime * 1000;
      setAt(ms);
      /* the cut is previewed as you drag it — the only way to know the cut you
         chose is the cut you meant */
      const t = edit.trim;
      if (t && (ms >= t.end - 60 || ms < t.start - 200)) v.currentTime = t.start / 1000;
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    meta();
    v.addEventListener('loadedmetadata', meta);
    v.addEventListener('timeupdate', tick);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('loadedmetadata', meta);
      v.removeEventListener('timeupdate', tick);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [isVideo, urls, i, edit.trim]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  /* ── placing things ── */
  /**
   * 🟥 THE SECOND STICKER MUST NOT LAND ON THE FIRST. Every one is dropped at
   *    the phone's own point — centred, 100 up — so placing a poll and then the
   *    loving indicator stacked two white cards on the same spot and looked
   *    like one broken widget. The phone gets away with it because you place
   *    one and drag it before reaching for the next; here the whole panel is a
   *    row of buttons and people press three.
   * 🟥 AND THE STEP HAS TO CLEAR A CARD, NOT A NUDGE. A poll is 220 points wide
   *    and about 150 tall; a 26-point diagonal offset left two white cards
   *    almost entirely on top of each other, which looked like one broken
   *    widget rather than two stickers. The step is a card's height.
   * 🟢 DOWN FIRST, THEN ACROSS — four fit down a phone before the column has to
   *    move, and down is where there is room: the byline owns the bottom left
   *    and the rail owns the right.
   */
  const place = (kind, extra) => {
    const base = newSticker(kind, `${Date.now()}${Math.random().toString(36).slice(2, 6)}`);
    const n = edit.stickers.length;
    const s = {
      ...base,
      x: base.x + Math.floor(n / 4) * 48,
      y: base.y + (n % 4) * 96,
      ...(extra || {}),
    };
    set({ ...edit, stickers: [...edit.stickers, s] });
    setSel(s.id);
  };
  const addImageSticker = (file) => {
    if (!file) return;
    place('gallery_image', { imageUrl: URL.createObjectURL(file), _file: file });
  };

  const selected = edit.stickers.find((s) => s.id === sel) || null;

  const TOOLS = [
    ['stickers', 'Stickers', Smile],
    ['text', 'Text', TypeIcon],
    ['filter', 'Filters', Sliders],
    ...(isVideo ? [['trim', 'Trim', Scissors], ['sound', 'Sounds', MusicIcon]] : []),
  ];

  return (
    <div className="mx-wrap" role="dialog" aria-label="Edit media" aria-modal="true">
      <header className="mx-bar">
        <button type="button" className="mx-plain" onClick={onCancel}>Cancel</button>

        <div className="mx-devices" role="tablist" aria-label="Preview device">
          {DEVICES.map((d) => (
            <button
              key={d.id} type="button" role="tab"
              aria-selected={d.id === deviceId}
              className={d.id === deviceId ? 'on' : ''}
              onClick={() => setDeviceId(d.id)}
            >
              {d.name}
            </button>
          ))}
        </div>

        <button type="button" className="mx-done" onClick={() => onDone(edits)}>
          <Check width={15} height={15} /> Done
        </button>
      </header>

      <div className="mx-body">
        <section className="mx-stage" ref={stageRef}>
          <Phone
            file={files[i]} url={urls[i]} edit={edit} set={set}
            device={device} zoom={z} videoRef={videoRef}
            me={me} caption={caption} sel={sel} setSel={setSel} playing={playing}
          />

          <div className="mx-stagebar">
            {isVideo && (
              <button type="button" className="mx-icon" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
                {playing ? <Pause width={15} height={15} /> : <Play width={15} height={15} />}
              </button>
            )}
            <label className="mx-zoom">
              <span>Zoom</span>
              <input
                type="range" min="0.3" max="1" step="0.01" value={z}
                aria-label="Zoom the preview"
                onChange={(e) => setManualZoom(Number(e.target.value))}
              />
              <b>{Math.round(z * 100)}%</b>
            </label>
            {manualZoom && (
              <button type="button" className="mx-plain sm" onClick={() => setManualZoom(null)}>Fit</button>
            )}
            {files.length > 1 && (
              <div className="mx-pages">
                {files.map((f, k) => (
                  <button
                    key={k} type="button" className={`mx-page ${k === i ? 'on' : ''}`}
                    aria-label={`Item ${k + 1}`} onClick={() => setI(k)}
                  >
                    {f.type?.startsWith('video/')
                      ? <video src={urls[k]} muted preload="metadata" />
                      : <img src={urls[k]} alt="" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="mx-panel">
          <nav className="mx-tabs" role="tablist" aria-label="Tools">
            {TOOLS.map(([key, label, Icon]) => (
              <button
                key={key} type="button" role="tab"
                aria-selected={tool === key}
                className={tool === key ? 'on' : ''}
                onClick={() => setTool(key)}
              >
                <Icon width={17} height={17} />
                <em>{label}</em>
              </button>
            ))}
          </nav>

          <div className="mx-panel-body">
            {tool === 'stickers' && (
              <>
                <p className="mx-label">Widgets</p>
                <div className="mx-widgets">
                  {WIDGET_KINDS.map((kind) => (
                    <button
                      key={kind} type="button" className="mx-widget"
                      onClick={() => (kind === 'gallery_image' ? stickerFileRef.current?.click() : place(kind))}
                    >
                      {kind === 'gallery_image' ? <ImageIcon width={14} height={14} /> : null}
                      {WIDGET_LABELS[kind]}
                    </button>
                  ))}
                  <input
                    ref={stickerFileRef} type="file" accept="image/*" hidden
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; addImageSticker(f); }}
                  />
                </div>
                <p className="mx-label">Emoji</p>
                {/* 🟢 THE APP'S OWN PANEL, not a second grid written for this
                    drawer — the status composer already has one with every
                    group in it, and two emoji pickers is how they drift. */}
                <EmojiPanel onPick={(e) => place('emoji', { emoji: e })} onClose={() => {}} />
              </>
            )}

            {tool === 'text' && (
              <>
                <textarea
                  className="mx-ta" rows={3} value={edit.text} placeholder="Write on the picture…"
                  aria-label="Text on the picture"
                  onChange={(e) => set({ ...edit, text: e.target.value })}
                />
                <p className="mx-label">Style</p>
                {/* 🟢 EACH LOOK IS SHOWN IN ITSELF. A row of the words
                    "shadow / box / neon" tells you their names; a row drawn in
                    the actual style tells you what you are choosing — which is
                    the complaint that started this rebuild. */}
                <div className="mx-modes">
                  {TEXT_MODES.map((m) => (
                    <button
                      key={m} type="button"
                      className={`mx-mode ${edit.mode === m ? 'on' : ''}`}
                      onClick={() => set({ ...edit, mode: m })}
                    >
                      <span style={overlayTextStyle({
                        overlayTextStyle: m, overlayTextSize: 17, overlayTextColor: edit.color, overlayTextAlign: 'center',
                      }, 1)}
                      >
                        Aa
                      </span>
                      <i>{m}</i>
                    </button>
                  ))}
                </div>
                <p className="mx-label">Colour</p>
                <div className="mx-swatches">
                  {TEXT_COLORS.map((c) => (
                    <button
                      key={c} type="button" className={`mx-sw ${edit.color === c ? 'on' : ''}`}
                      style={{ background: c }} title={c} aria-label={c}
                      onClick={() => set({ ...edit, color: c })}
                    />
                  ))}
                </div>
                <p className="mx-label">Alignment</p>
                <div className="mx-seg">
                  {['left', 'center', 'right'].map((a) => (
                    <button key={a} type="button" className={edit.align === a ? 'on' : ''} onClick={() => set({ ...edit, align: a })}>{a}</button>
                  ))}
                </div>
                <label className="mx-range">
                  <span>Size</span>
                  <input
                    type="range" min="12" max="72" step="1" value={edit.size}
                    onChange={(e) => set({ ...edit, size: Number(e.target.value) })}
                  />
                  <b>{edit.size}</b>
                </label>
              </>
            )}

            {tool === 'filter' && (
              <>
                <div className="mx-filters">
                  {EDITOR_FILTERS.map((f) => (
                    <button
                      key={f.id} type="button"
                      className={`mx-filter ${edit.filter === f.id ? 'on' : ''}`}
                      onClick={() => set({ ...edit, filter: f.id })}
                    >
                      <span className="mx-chip">
                        {isVideo
                          ? <video src={urls[i]} muted preload="metadata" />
                          : <img src={urls[i]} alt="" />}
                        {f.color !== 'transparent' && <i style={{ background: f.color, mixBlendMode: 'overlay' }} />}
                      </span>
                      <em>{f.id}</em>
                    </button>
                  ))}
                </div>
                {/* 🟢 THE STRENGTH SLIDER ONLY EXISTS WHEN THERE IS A GRADE TO
                    WEAKEN. A 0–100 control under "pure" adjusts nothing and
                    reads as broken. */}
                {edit.filter !== 'pure' && (
                  <label className="mx-range">
                    <span>Strength</span>
                    <input
                      type="range" min="0" max="100" step="1" value={edit.intensity}
                      onChange={(e) => set({ ...edit, intensity: Number(e.target.value) })}
                    />
                    <b>{edit.intensity}</b>
                  </label>
                )}
              </>
            )}

            {tool === 'trim' && isVideo && (
              <TrimPanel videoRef={videoRef} edit={edit} set={set} dur={dur} at={at} />
            )}
            {tool === 'sound' && isVideo && (
              <SoundPanel sound={edit.sound} onChange={(sound) => set({ ...edit, sound })} videoRef={videoRef} dur={dur} />
            )}
          </div>

          {/* 🟢 THE SELECTED STICKER'S CONTROLS LIVE IN THE PANEL, not floating
              on the canvas. A size slider pinned under a sticker is a slider
              that is off the screen whenever the sticker is near an edge —
              which is most of the time, because that is where people put them. */}
          {selected && (
            <footer className="mx-inspector">
              <p className="mx-label">{WIDGET_LABELS[selected.type] || 'Sticker'}</p>
              <label className="mx-range">
                <span>Size</span>
                <input
                  type="range" min="0.35" max="5" step="0.05" value={stickerScale(selected)}
                  aria-label="Sticker size"
                  onChange={(e) => set({
                    ...edit,
                    stickers: edit.stickers.map((x) => (x.id === selected.id ? { ...x, scale: Number(e.target.value) } : x)),
                  })}
                />
                <b>{Math.round(stickerScale(selected) * 100)}%</b>
              </label>
              <div className="mx-inspector-acts">
                <button
                  type="button" className="mx-plain sm"
                  onClick={() => set({ ...edit, stickers: edit.stickers.map((x) => (x.id === selected.id ? { ...x, x: 0, y: 0 } : x)) })}
                >
                  Centre
                </button>
                <button
                  type="button" className="mx-danger"
                  onClick={() => { set({ ...edit, stickers: edit.stickers.filter((x) => x.id !== selected.id) }); setSel(null); }}
                >
                  <Trash width={13} height={13} /> Remove
                </button>
              </div>
            </footer>
          )}
        </aside>
      </div>
    </div>
  );
}

/* the note glyph for the Sounds tab */
const MusicIcon = ({ width = 17, height = 17 }) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
  </svg>
);

const WIDGET_LABELS = {
  time: 'Time',
  time_24: '24-hour',
  clock: 'Clock',
  date: 'Date',
  location: 'Location',
  gallery_image: 'Image',
  poll: 'Poll',
  slider: 'Loving it',
  emoji: 'Emoji',
};
